import { formatDate } from '../../../Fees/RateContextHeader/RateContextHeader';
import { hasCompanyName } from '../../customerName';
import { CustomerRow } from '../../CustomersV2.types';

type SubStages = CustomerRow['application_sub_stage_object'];
type SubStageKey = keyof SubStages;

const SUB_STAGE_LABELS: Record<SubStageKey, string> = {
  business_type: 'Sign-Up (Business type)',
  business_info: 'Sign-Up (Business info)',
  director_info: 'Sign-Up (Director info)',
  id_check: 'ID checks',
  card_details: 'Card details',
  internal_checks: 'Internal checks (no action needed)',
  shareholder_check: 'Major shareholder checks',
  personal_guarantor_check: 'Agreements signed',
};

const BUSINESS_DETAILS_STEPS: SubStageKey[] = [
  'business_type',
  'business_info',
  'director_info',
];

const SECURITY_STEPS: SubStageKey[] = [
  'id_check',
  'card_details',
  'internal_checks',
  'shareholder_check',
  'personal_guarantor_check',
];

export interface ProfileStep {
  key: SubStageKey;
  label: string;
  completed: boolean;
}

export interface ProfileStage {
  key: 'business_details' | 'security';
  label: string;
  number: 1 | 2;
  status: 'complete' | 'current' | 'upcoming';
  completedCount: number;
  requiredCount: number;
  steps: ProfileStep[];
}

const buildSteps = (subStages: SubStages, keys: SubStageKey[]): ProfileStep[] =>
  keys
    .filter((key) => subStages[key]?.required)
    .map((key) => ({
      key,
      label: SUB_STAGE_LABELS[key],
      completed: Boolean(subStages[key]?.completed),
    }));

export const getProfileStages = (row: CustomerRow): ProfileStage[] => {
  const subStages = row.application_sub_stage_object;
  const isSecurity = row.application_stage.value === 'security';

  const businessSteps = buildSteps(subStages, BUSINESS_DETAILS_STEPS);
  // Completed-first, so finished checks surface at the top of the list. Sort is
  // stable, so canonical order is preserved inside each group.
  const securitySteps = [...buildSteps(subStages, SECURITY_STEPS)].sort(
    (a, b) => Number(b.completed) - Number(a.completed),
  );

  return [
    {
      key: 'business_details',
      label: 'Business details',
      number: 1,
      status: isSecurity ? 'complete' : 'current',
      completedCount: businessSteps.filter((step) => step.completed).length,
      requiredCount: businessSteps.length,
      steps: businessSteps,
    },
    {
      key: 'security',
      label: 'Security checks',
      number: 2,
      status: isSecurity ? 'current' : 'upcoming',
      completedCount: securitySteps.filter((step) => step.completed).length,
      requiredCount: securitySteps.length,
      steps: securitySteps,
    },
  ];
};

export const getProfileTitle = (row: CustomerRow): string => {
  if (hasCompanyName(row)) return row.company_name;

  const date = new Date(row.earliest_interacted_date);
  const pad = (value: number) => String(value).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

  return `Customer - ${formatDate(row.earliest_interacted_date, 'short')} | ${time}`;
};
